import { z } from "zod";

import { SECTION_IDS } from "./organizations";
import { workItemIdSchema } from "./work";

// Notifications: what the console noticed for one member, shown under the bell in the top bar and on /notifications.
// The API and the frontend share these. The intake copier raises one when a document someone uploaded is already in
// Intake; once Bedrock has read a document, the processor that runs its agents tells whoever uploaded it which parties
// the Directory agent added from it; and the demo organization seeds a few for its owner.

/** The pages a notification can open: routes of the console, so the frontend's typed links take them as they are. */
export const NOTIFICATION_TARGETS = [
	"/work",
	"/directory",
	"/intake",
	"/finance",
	"/legal",
	"/people",
	"/equity",
] as const;
export type NotificationTarget = (typeof NOTIFICATION_TARGETS)[number];

/**
 * What a notification is, which the bell and the Notifications page mark it by and the page filters on: `work` names
 * the work items it asks the member to do, and `notice` tells them anything else — an upload that was already in
 * Intake, say.
 */
export const NOTIFICATION_KINDS = ["work", "notice"] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const notificationSchema = z.object({
	id: z.string().min(1),
	/** What it is, one of `NOTIFICATION_KINDS`. */
	kind: z.enum(NOTIFICATION_KINDS),
	/** When it was raised, as an ISO 8601 timestamp in UTC. */
	at: z.iso.datetime(),
	/** The business domain it belongs to, which colors its dot, or null for the workspace itself. */
	section: z.enum(SECTION_IDS).nullable(),
	title: z.string().min(1).max(160),
	description: z.string().max(320),
	/** The page it is about: the bell's row opens it, and the Notifications page's side panel links to it. */
	to: z.enum(NOTIFICATION_TARGETS),
	/** Archived ones are out of the bell's count and live on the Archived tab. */
	archived: z.boolean(),
	/**
	 * The work items it is about, by id, in the order they were raised: the ones the Directory agent raised for the
	 * parties it added from a document. Only a `work` notification carries any, and every one of them does. At most ten,
	 * twice the five parties one document may add.
	 */
	workItemIds: z.array(workItemIdSchema).max(10).optional(),
});
export type Notification = z.infer<typeof notificationSchema>;

/** Which of a member's notifications a page lists: the ones they have not dealt with, or the ones they archived. */
export const NOTIFICATION_STATES = ["unattended", "archived"] as const;
export type NotificationState = (typeof NOTIFICATION_STATES)[number];

/** The bell and the Notifications page come a page of this many notifications at a time, newest first. */
export const NOTIFICATIONS_PAGE_SIZE = 10;

/** A link to another page of notifications. The API makes it; a client passes it back unchanged and never reads it. */
export const notificationsPageTokenSchema = z.string().regex(/^[\w-]{1,512}$/, "Invalid page");

/**
 * The query of `GET /api/notifications`: which of the member's notifications to list — the ones in a state, and only
 * those of one kind when it names one — and the page to show, the newest when there is none. The bell asks for no
 * state and no kind, and gets every unattended one, which is what it counts.
 */
export const notificationsQuerySchema = z.object({
	state: z.enum(NOTIFICATION_STATES).default("unattended"),
	kind: z.enum(NOTIFICATION_KINDS).optional(),
	page: notificationsPageTokenSchema.optional(),
});
export type NotificationsQuery = z.infer<typeof notificationsQuerySchema>;

/** One page of a member's notifications, newest first, with links to the pages beside it: `null` where there is none. */
export const notificationsPageSchema = z.object({
	notifications: z.array(notificationSchema).max(NOTIFICATIONS_PAGE_SIZE),
	/**
	 * How many the member has in this state, of the kind asked for or of every kind, every page counted: the bell's
	 * badge and the page's heading.
	 */
	total: z.int().nonnegative(),
	/** The member's notifications in this state counted by kind, whichever kind was asked for: the page's filters. */
	counts: z.record(z.enum(NOTIFICATION_KINDS), z.int().nonnegative()),
	/** Older notifications. */
	next: notificationsPageTokenSchema.nullable(),
	/** Newer notifications. */
	previous: notificationsPageTokenSchema.nullable(),
});
export type NotificationsPage = z.infer<typeof notificationsPageSchema>;

/** Archives a notification, or restores it. */
export const updateNotificationRequestSchema = z.object({ archived: z.boolean() });
export type UpdateNotificationRequest = z.infer<typeof updateNotificationRequestSchema>;
